import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Footer from '../../components/Footer';
import { getActivePlans, Plan } from '../../services/planService';
import { assignPlanToArtist } from '../../services/artistSubscriptionService';

interface LocationState {
  artistId: number;
  artistName: string;
  email: string;
}

const PlanSelection: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;

  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Redirect if no artist data
    if (!state || !state.artistId) {
      navigate('/signup');
      return;
    }

    fetchPlans();
  }, [state, navigate]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const activePlans = await getActivePlans();
      setPlans(activePlans);

      // Preselect default plan
      const defaultPlan = activePlans.find((p) => p.isDefault);
      if (defaultPlan) {
        setSelectedPlan(defaultPlan);
      } else if (activePlans.length > 0) {
        setSelectedPlan(activePlans[0]);
      }
    } catch (err: any) {
      setError('Failed to load plans. Please try again.');
      console.error('Error fetching plans:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedPlan) {
      setError('Please select a plan');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await assignPlanToArtist({
        artistId: state.artistId,
        planId: selectedPlan.planId,
        transactionId: undefined,
      });

      // Show success and navigate to login
      navigate('/signup/success', {
        state: {
          artistName: state.artistName,
          email: state.email,
          planName: selectedPlan.planName,
        },
      });
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to assign plan. Please try again.';
      setError(String(msg));
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-secondary">Loading plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col pb-16 bg-background">
      <div className="max-w-4xl mx-auto py-12 px-4 flex-1">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-semibold text-primary mb-2">Choose Your Plan</h1>
          <p className="text-secondary">Select a subscription plan to complete your registration</p>
        </div>

        {error && (
          <div className="bg-red-100 text-red-600 p-3 mb-6 rounded-lg max-w-2xl mx-auto">
            {error}
          </div>
        )}

        {plans.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-secondary/20 text-center">
            <p className="text-secondary">No plans available at the moment. Please contact support.</p>
          </div>
        ) : (
          <>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-8">
              {plans.map((plan) => (
                <div
                  key={plan.planId}
                  onClick={() => setSelectedPlan(plan)}
                  className={`bg-white rounded-2xl shadow-lg p-6 border-2 cursor-pointer transition-all ${
                    selectedPlan?.planId === plan.planId
                      ? 'border-primary ring-2 ring-primary/30 transform scale-105'
                      : 'border-secondary/20 hover:border-primary/50'
                  }`}
                >
                  {plan.isDefault && (
                    <div className="bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full inline-block mb-3">
                      RECOMMENDED
                    </div>
                  )}
                  <h3 className="text-2xl font-bold text-primary mb-2">{plan.planName}</h3>
                  <div className="text-3xl font-bold text-secondary mb-4">
                    ${plan.price}
                    <span className="text-sm font-normal text-secondary/70">
                      /{plan.durationInDays} days
                    </span>
                  </div>
                  {plan.description && (
                    <p className="text-secondary/80 text-sm mb-4">{plan.description}</p>
                  )}
                  <div className="pt-4 border-t border-secondary/10">
                    <p className="text-sm text-secondary/70">
                      Valid for <strong>{plan.durationInDays} days</strong>
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="max-w-md mx-auto">
              <button
                onClick={handleConfirm}
                disabled={!selectedPlan || submitting}
                className="w-full btn-primary py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Completing Signup...' : `Confirm ${selectedPlan?.planName || 'Plan'}`}
              </button>

              <p className="text-center text-sm text-secondary/70 mt-4">
                By confirming, you agree to our terms of service
              </p>
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default PlanSelection;
